import { Link } from "react-router-dom";
import { featuredStories } from "../../../content";
import type { Story } from "../../../types/content";
import { categoryAccent, categoryLabel } from "./Stories";

/**
 * ============================================================================
 * Highlighted Stories — the home page's teaser strip for the /stories route.
 * ============================================================================
 *
 * Reads `featuredStories` from the loader (Constitution V.4). The label and
 * accent helpers are imported from Stories rather than re-declared, so a
 * category can never read one way on the home page and another on /stories.
 *
 * Summary only, deliberately: the Situation/Action/Result body belongs on the
 * page built for it. Each card deep-links to its own story.
 *
 * Renders nothing when no story is featured — Article II.4 forbids shipping an
 * empty section, and that has to be a property of the component rather than of
 * whoever edits the JSON next.
 */

/**
 * Spelled-out counts read better than numerals in the standfirst. The list
 * covers every plausible strip size and falls back to the numeral, so the
 * sentence can never disagree with the data — which it did until 2026-08-10,
 * when the strip grew to four while the copy still said "Three".
 */
const COUNT_WORDS: ReadonlyArray<string> = ["No", "One", "Two", "Three", "Four", "Five", "Six"];

const countWord = (n: number): string => COUNT_WORDS[n] ?? String(n);

const HighlightedStories = (): JSX.Element | null => {
	if (!featuredStories.length) return null;

	const count = featuredStories.length;

	return (
		<section className="section-shell" id="stories" aria-labelledby="highlighted-stories-heading">
			<div className="section-rail">
				{/* Owner ruling 2026-08-19 (D-063): every section carries an eyebrow.
				    Five were added in the noun-phrase register, matching
				    Certifications' existing "Verified Credentials". These are
				    structural labels rather than claims about the author, which is
				    why they live in the component like the two that already did —
				    II.2 forbids hardcoding a career FACT, and a section label is
				    not one. */}
				<p className="section-eyebrow">Selected Engagements</p>

				<h2 id="highlighted-stories-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Highlighted
					</span>{" "}
					Stories
				</h2>

				<p className="section-standfirst">
					{countWord(count)} {count === 1 ? "engagement" : "engagements"} in full — the situation, the
					decisions, and what changed.
				</p>

				{/* Two across from lg, never three. The strip held three cards until
				    2026-08-10 and used lg:grid-cols-3; at four cards that same grid
				    leaves a lone card on a second row beside two empty cells. Two
				    columns divide four evenly and give each card more width than three
				    ever did. Single column below lg stands: at md the three-across grid
				    gave 212px cards, and a card that opens with an uppercase category
				    chip — "Healthcare (Regulated)" — has no business being 212px wide.
				    Measured, not guessed. */}
				<div className="mt-12 grid w-full grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
					{featuredStories.map((story: Story) => (
						/* The card is the LINK, so the shell is an <a> here and an
						   <article> everywhere else. That is the reason `card-shell`
						   is a class and not a component: the treatment is identical,
						   the element it lands on is not (D-061). */
						<Link key={story.id} to={`/stories#${story.id}`} className="card-shell no-underline">
							<div className="card-face flex flex-col p-6">
								<span
									className={`pill-tint pill-tint-upper pill-tint-wrap self-start ${categoryAccent(
										story.category
									)}`}
								>
									{categoryLabel(story.category)}
								</span>

								<h3 className="mt-4 text-lg font-bold text-[var(--text-strong)]">{story.title}</h3>

								<p className="mt-2 text-sm text-[var(--text-muted)]">
									{story.organization} · {story.period}
								</p>

								<p className="mt-4 text-sm leading-relaxed text-[var(--text-secondary)]">
									{story.summary}
								</p>

								<div className="mt-auto flex flex-wrap gap-2 pt-5">
									{story.tags.map((tag: string) => (
										<span key={tag} className="chip-quiet">
											{tag}
										</span>
									))}
								</div>

								<span aria-hidden="true" className="mt-5 text-sm font-medium text-[var(--accent-link)]">
									Read the full story →
								</span>
							</div>
						</Link>
					))}
				</div>

				<div className="mt-10 text-center">
					<Link
						to="/stories"
						className="inline-block rounded-full border border-[var(--accent-marker)] px-5 py-2 text-sm font-medium text-[var(--text-strong)] no-underline transition-colors duration-200 hover:bg-[var(--accent-marker-fill)]"
					>
						All stories →
					</Link>
				</div>
			</div>
		</section>
	);
};

export default HighlightedStories;
